/**
 * InviteForm — email + seat type selector + Send Invite button.
 *
 * Props:
 *   tenantId   — string  (the current tenant's UUID)
 *   seatTypes  — [{ id, slug, name }]
 *   dark       — boolean
 *   onSent()   — called after a successful invite so the parent can refresh the list
 */

import { useState } from 'react'
import { sendInvite } from '../../services/tenancy.js'
import Select from '../Select.jsx'

export default function InviteForm({ tenantId, seatTypes, dark, onSent }) {
  const staffType = seatTypes.find(st => st.slug === 'staff') || seatTypes[0]
  const [email,      setEmail]      = useState('')
  const [seatTypeId, setSeatTypeId] = useState(staffType?.id || '')
  const [employment, setEmployment] = useState('employee')   // 'employee' | 'contractor' — seeds the time-gate policy on accept
  const [sending,    setSending]    = useState(false)
  const [error,      setError]      = useState(null)
  const [success,    setSuccess]    = useState(null)

  const textPrim  = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMuted = dark ? 'rgba(91,132,148,0.80)'  : 'rgba(7,39,45,0.50)'
  const inputBg   = dark ? 'rgba(255,255,255,0.05)' : '#f4f7f8'
  const inputBdr  = dark ? 'rgba(255,255,255,0.12)' : 'rgba(7,39,45,0.18)'

  async function handleSend() {
    const trimmed = email.trim()
    if (!trimmed || !seatTypeId || !tenantId) return
    setSending(true)
    setError(null)
    setSuccess(null)
    try {
      await sendInvite(tenantId, trimmed, seatTypeId, employment)
      setSuccess(`Invite sent to ${trimmed}`)
      setEmail('')
      if (onSent) onSent()
    } catch (err) {
      setError(err.message || 'Could not send invite')
    } finally {
      setSending(false)
    }
  }

  const S = {
    row: {
      display: 'flex', gap: 8, alignItems: 'flex-end',
      padding: '16px 20px', flexWrap: 'wrap',
    },
    group: { display: 'flex', flexDirection: 'column', gap: 4 },
    label: {
      fontFamily: '"DM Mono", monospace', fontSize: 10,
      letterSpacing: '0.08em', textTransform: 'uppercase', color: textMuted,
    },
    input: {
      fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: textPrim,
      background: inputBg, border: `0.5px solid ${inputBdr}`,
      borderRadius: 4, padding: '7px 10px', outline: 'none', minWidth: 240,
    },
    select: {
      fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: textPrim,
      background: inputBg, border: `0.5px solid ${inputBdr}`,
      borderRadius: 4, padding: '7px 10px', cursor: 'pointer', outline: 'none',
    },
    btn: {
      fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 500,
      background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 4,
      padding: '7px 16px', cursor: sending ? 'default' : 'pointer',
      opacity: (sending || !email.trim()) ? 0.55 : 1, whiteSpace: 'nowrap',
    },
    msg: {
      fontFamily: '"DM Sans", sans-serif', fontSize: 12,
      padding: '0 20px 12px',
    },
  }

  return (
    <div>
      <div style={S.row}>
        <div style={S.group}>
          <div style={S.label}>Email address</div>
          <input
            type="email"
            value={email}
            onChange={e => { setEmail(e.target.value); setError(null); setSuccess(null) }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSend() } }}
            placeholder="name@example.com"
            style={S.input}
            disabled={sending}
          />
        </div>
        <div style={S.group}>
          <div style={S.label}>Seat type</div>
          <Select
            value={seatTypeId}
            onChange={setSeatTypeId}
            options={seatTypes.map(st => ({ value: st.id, label: st.name }))}
            dark={dark}
            disabled={sending}
          />
        </div>
        <div style={S.group}>
          <div style={S.label}>Employment</div>
          <Select
            value={employment}
            onChange={setEmployment}
            options={[{ value: 'employee', label: 'Employee' }, { value: 'contractor', label: 'Contractor' }]}
            dark={dark}
            disabled={sending}
          />
        </div>
        <button style={S.btn} disabled={sending || !email.trim()} onClick={handleSend}>
          {sending ? 'Sending…' : 'Send Invite'}
        </button>
      </div>
      {error   && <div style={{ ...S.msg, color: '#c0392b' }}>{error}</div>}
      {success && <div style={{ ...S.msg, color: '#1D9E75' }}>{success}</div>}
    </div>
  )
}
